import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail, Info, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FloatInput({ label, id, type = "text", value, onChange, required, placeholder }) {
  return (
    <div className="relative">
      <label htmlFor={id} className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none">
        {label}{required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        id={id} type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors placeholder:text-gray-300"
      />
    </div>
  );
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) { setError("Please enter your email address."); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f2f2f2] flex flex-col" data-testid="forgot-password-page">
      <div className="pt-5 pb-2 px-6">
        <button onClick={() => navigate("/signin")} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors" data-testid="back-to-signin-link">
          <ArrowLeft size={16} /> Back to Sign In
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-[440px] p-8 sm:p-10" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-password-success">
              <div className="flex justify-center mb-5">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                  <Mail size={40} className="text-green-500" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Check your inbox</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                If <span className="font-semibold text-gray-700">{email}</span> is registered, you'll receive a password reset link shortly.
              </p>
              <p className="text-xs text-gray-400 mb-8">Didn't receive it? Check your spam folder or try again.</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => { setSent(false); setEmail(""); }}
                  className="w-full border border-gray-300 hover:border-gray-400 text-gray-700 font-medium py-3 rounded-full transition-colors text-sm"
                  data-testid="try-again-btn"
                >
                  Try a different email
                </button>
                <button
                  onClick={() => navigate("/signin")}
                  className="w-full bg-[#ED2E24] hover:bg-[#c9231a] text-white font-semibold py-3 rounded-full transition-colors text-sm"
                  data-testid="back-signin-btn"
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Reset password</h1>
                <p className="text-gray-400 text-sm mt-1.5">Enter your email and we'll send a reset link</p>
              </div>

              {error && (
                <div className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl flex items-start gap-2" data-testid="forgot-password-error">
                  <Info size={15} className="mt-0.5 shrink-0" /><span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate data-testid="forgot-password-form">
                <FloatInput label="Email" id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@xyz.com" />
                <button
                  type="submit" disabled={loading}
                  className="mt-6 w-full bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-70 text-white font-semibold py-3.5 rounded-full transition-colors text-sm"
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? "Sending…" : "Send Reset Link"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
